import { FC, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid2 as Grid,
  Stack,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ErrorMessage, Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import { TagManager } from "components/TagManager";
import { ListWithEntitySelector } from "components/ListWithEntitySelector";
import { Tag } from "types/tag";
import { getAcademicYearDates } from "utils/Datetime";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { userSelector } from "../../../reduxStore/auth/selectors";
import { User } from "types/user";

export type DetailsFormProps = {
  tags: Tag[];
  coaches: User[];
  selectedCoaches: User[];
  mentorsOptional?: boolean;
  onAddTag: (tag: Tag) => void;
  onRemoveTag: (tag: Tag) => void;
  onAddCoach: (coach: User) => void;
  onRemoveCoach: (coachId: string) => void;
  onChangeCoaches: (users: User[]) => void;
  onBack: () => void;
  onComplete: (values: {
    name: string;
    start_date: string;
    end_date: string;
  }) => void;
};

export const GradClassCreator: FC<DetailsFormProps> = ({
  tags,
  coaches,
  selectedCoaches,
  mentorsOptional = false,
  onAddTag,
  onRemoveTag,
  onChangeCoaches,
  onBack,
  onComplete,
}) => {
  const user = useAppSelector(userSelector);
  const [skipMentors, setSkipMentors] = useState(false);
  const initialValues = useMemo(() => {
    const { start_date, end_date } = getAcademicYearDates();
    return {
      start_date: dayjs(start_date),
      end_date: dayjs(end_date),
      mentor_id: "",
    };
  }, []);

  const mentorsRequired = !mentorsOptional && !skipMentors;
  const mentorsMissing = mentorsRequired && selectedCoaches.length === 0;

  const formik = useFormik<{
    start_date: Dayjs | null;
    end_date: Dayjs | null;
    mentor_id: string;
  }>({
    initialValues,
    validationSchema: Yup.object({
      start_date: Yup.mixed<Dayjs | null>()
        .test(
          "start-required",
          "Start date is required",
          (v) => !!v && dayjs.isDayjs(v) && v.isValid(),
        )
        .nullable(),
      end_date: Yup.mixed<Dayjs | null>()
        .test(
          "end-required",
          "End date is required",
          (v) => !!v && dayjs.isDayjs(v) && v.isValid(),
        )
        .test(
          "end-after-start",
          "End date must be after start date",
          (v, ctx) => {
            const start = ctx.parent.start_date as Dayjs | null;
            return !!v && !!start
              ? v.isAfter(start) || v.isSame(start, "day")
              : true;
          },
        )
        .nullable(),
    }),
    onSubmit: () => {
      if (mentorsMissing) {
        return;
      }
      onComplete({
        name: `${user?.name}-${formik.values.start_date?.format("YYYY-MM-DD")}`,
        start_date: formik.values.start_date?.format("YYYY-MM-DD") || "",
        end_date: formik.values.end_date?.format("YYYY-MM-DD") || "",
      });
    },
  });

  return (
    <FormikProvider value={formik}>
      <Form noValidate onSubmit={formik.handleSubmit}>
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Details
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Mentors{mentorsOptional ? " (optional)" : ""}
              </Typography>
              {mentorsOptional && (
                <FormControlLabel
                  sx={{ mb: 1 }}
                  control={
                    <Checkbox
                      checked={skipMentors}
                      onChange={(_, checked) => {
                        setSkipMentors(checked);
                        if (checked) {
                          onChangeCoaches([]);
                          formik.setFieldValue("mentor_id", "");
                        }
                      }}
                    />
                  }
                  label="Continue without a mentor"
                />
              )}
              {!skipMentors && (
                <Box>
                  <ListWithEntitySelector
                    elements={selectedCoaches}
                    onChangeElements={(users: User[]) => {
                      onChangeCoaches(users);
                      const first = users[0]?.user_id || "";
                      formik.setFieldValue("mentor_id", first);
                    }}
                    idField="user_id"
                    entityName="Mentor"
                    entityList={coaches}
                    displayField="name"
                    displayFieldSecondary="email"
                    error={
                      formik.submitCount > 0 && mentorsMissing
                        ? "Please select at least one mentor."
                        : ""
                    }
                  />
                </Box>
              )}
              {mentorsOptional && skipMentors && (
                <Typography variant="body2" color="text.secondary">
                  You can add a mentor later from your profile if needed.
                </Typography>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start date"
                  value={formik.values.start_date}
                  sx={{ width: "100%" }}
                  onChange={(newValue) => {
                    formik.setFieldValue("start_date", newValue);
                  }}
                />
              </LocalizationProvider>
              <Typography variant="caption" color="error">
                <ErrorMessage name="start_date" />
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="End date"
                  value={formik.values.end_date}
                  sx={{ width: "100%" }}
                  onChange={(newValue) => {
                    formik.setFieldValue("end_date", newValue);
                  }}
                />
              </LocalizationProvider>
              <Typography variant="caption" color="error">
                <ErrorMessage name="end_date" />
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Tags
              </Typography>
              <TagManager
                tags={tags}
                onAdd={onAddTag}
                onDelete={onRemoveTag}
                isEditing={true}
              />
            </Grid>
          </Grid>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ mt: 3 }}
          >
            <Button variant="outlined" onClick={onBack}>
              Back
            </Button>
            <Button variant="contained" type="submit">
              Complete
            </Button>
          </Stack>
        </Box>
      </Form>
    </FormikProvider>
  );
};
